import { HttpError } from '@littlebird/auth-client';

import type { Auth0LoginConfig, TokenExchangeResponse } from './types';

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

/** Send a passwordless OTP code to the given email via Auth0. */
export async function sendOtpCode(
  auth0: Auth0LoginConfig,
  email: string
): Promise<void> {
  const response = await fetch(`https://${auth0.domain}/passwordless/start`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id: auth0.clientId,
      connection: 'email',
      email,
      send: 'code',
      authParams: {
        scope: 'openid profile email offline_access',
      },
    }),
  });

  if (!response.ok) {
    throw new HttpError(response, await readJsonBody(response));
  }
}

/** Verify an OTP code and exchange it for tokens. */
export async function verifyOtpCode(
  auth0: Auth0LoginConfig,
  email: string,
  code: string,
  deviceId: string
): Promise<TokenExchangeResponse> {
  const data = new URLSearchParams({
    client_id: auth0.clientId,
    username: email,
    audience: auth0.audience,
    scope: 'openid profile email offline_access',
    grant_type: 'http://auth0.com/oauth/grant-type/passwordless/otp',
    otp: code,
    realm: 'email',
    connection: 'email',
    device: deviceId,
  });

  const response = await fetch(`https://${auth0.domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: data,
  });

  if (!response.ok) {
    throw new HttpError(response, await readJsonBody(response));
  }

  return response.json();
}

const PASSWORD_LOGIN_EMAILS = [
  'reviewer@anthropic.com',
  'reviewer@openai.com',
  'reviewer@muse.ai',
  'reviewer@grok.com',
];

export function isPasswordLoginEmail(email: string): boolean {
  return PASSWORD_LOGIN_EMAILS.includes(email.trim().toLowerCase());
}

export async function signInWithPassword(
  auth0: Auth0LoginConfig,
  email: string,
  password: string,
  deviceId: string
): Promise<TokenExchangeResponse> {
  const data = new URLSearchParams({
    client_id: auth0.clientId,
    username: email,
    password,
    audience: auth0.audience,
    scope: 'openid profile email offline_access',
    grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
    realm: 'Username-Password-Authentication',
    device: deviceId,
  });

  const response = await fetch(`https://${auth0.domain}/oauth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: data,
  });

  if (!response.ok) {
    throw new HttpError(response, await readJsonBody(response));
  }

  return response.json();
}
